// Onboarding tour for first-time players — highlights key HUD elements and
// main action targets via a dimmed overlay with a "hole" around the target.
// Shown once per user (persisted in localStorage `ac_onboarded_v1`), and
// re-playable from Settings.
import React, { useEffect, useRef, useState, useCallback } from "react";
import ReactDOM from "react-dom";
import { X, ChevronRight, Sparkles } from "lucide-react";

const ONBOARDED_KEY = "ac_onboarded_v1";

export function hasSeenOnboarding() {
  try {
    return localStorage.getItem(ONBOARDED_KEY) === "1";
  } catch {
    return false;
  }
}

export function markOnboardingSeen() {
  try {
    localStorage.setItem(ONBOARDED_KEY, "1");
  } catch { /* ignore */ }
}

export function resetOnboarding() {
  try {
    localStorage.removeItem(ONBOARDED_KEY);
  } catch { /* ignore */ }
}

// Step definitions. `target` is a CSS selector; null means centered modal.
// Each step has IT and EN text for title + body.
function buildSteps(isIt) {
  return [
    {
      target: null,
      expression: "happy",
      title: isIt ? "Benvenuto, Avventuriero" : "Welcome, Adventurer",
      body: isIt
        ? "Sei stato trasportato in un mondo fantastico. Ti guiderò in un rapido tour (30 secondi) per scoprire le funzioni principali."
        : "You have been transported into a fantasy world. Let me guide you through a quick 30-second tour of the main features.",
    },
    {
      target: '[data-testid="player-hp"]',
      expression: "determined",
      title: isIt ? "Vita ed Energia" : "Health & Energy",
      body: isIt
        ? "Qui vedi i Punti Vita (HP) e l'Oro. Perdi HP in battaglia — attento a non arrivare a zero! L'oro serve a comprare carte e oggetti."
        : "This shows your Health Points (HP) and Gold. You lose HP in battle — avoid reaching zero! Gold is used to buy cards and items.",
    },
    {
      target: '[data-testid="daily-quests-button"]',
      expression: "playful",
      title: isIt ? "Missioni Giornaliere" : "Daily Quests",
      body: isIt
        ? "Ogni giorno ricevi 3 missioni. Completale tutte per ottenere un bonus streak crescente in oro."
        : "Every day you get 3 missions. Complete all three to earn a growing streak bonus in gold.",
    },
    {
      target: '[data-testid="settings-button"]',
      expression: "curious",
      title: isIt ? "Impostazioni" : "Settings",
      body: isIt
        ? "Da qui puoi cambiare lo stile delle immagini generate (anime, Ghibli, pixel art...), la lingua, font, musica ed effetti."
        : "Here you can change the generated image style (anime, Ghibli, pixel art...), language, font, music and SFX.",
    },
    {
      target: '[data-testid="tab-story"]',
      expression: "curious",
      title: isIt ? "Il Libro della Storia" : "The Story Book",
      body: isIt
        ? "Questa è la tab principale: il libro che si scrive pagina per pagina in base alle tuoi scelte. Premi 'Apri il libro' per iniziare."
        : "This is the main tab: the book that writes itself page by page based on your choices. Tap 'Open the book' to begin.",
    },
    {
      target: '[data-testid="tab-inventory"]',
      expression: "playful",
      title: isIt ? "Inventario" : "Inventory",
      body: isIt
        ? "Qui trovi carte, armi, armature e alleati. Equipaggia gli oggetti per potenziare il personaggio; usa pozioni anche in battaglia."
        : "Here you find cards, weapons, armor and allies. Equip items to boost your character; use potions even in battle.",
    },
    {
      target: '[data-testid="tab-collection"]',
      expression: "curious",
      title: isIt ? "Collezione & Scambi" : "Collection & Trades",
      body: isIt
        ? "La Collezione traccia tutte le carte che hai ottenuto o perso, incluse quelle foil. Nella tab Scambi puoi mandare carte ad altri giocatori."
        : "The Collection tracks all cards you have obtained or lost, including foils. In the Trade tab you can send cards to other players.",
    },
    {
      target: '[data-testid="tab-shop"]',
      expression: "happy",
      title: isIt ? "Negozio" : "Shop",
      body: isIt
        ? "Compra pozioni, equipaggiamento, bustine di carte. Puoi convertire oro in Polvere Arcana per creare carte rare."
        : "Buy potions, equipment, card packs. You can convert gold into Arcane Dust to craft rare cards.",
    },
    {
      target: null,
      expression: "happy",
      title: isIt ? "Buon viaggio!" : "Safe travels!",
      body: isIt
        ? "Puoi rivedere questo tutorial in qualsiasi momento dalle Impostazioni → 'Rivedi tutorial'. Che il tuo cammino sia ricco di gloria."
        : "You can replay this tutorial anytime from Settings → 'Replay tutorial'. May your path be full of glory.",
    },
  ];
}

function getRect(selector) {
  if (!selector) return null;
  const el = document.querySelector(selector);
  if (!el) return null;
  const r = el.getBoundingClientRect();
  if (r.width === 0 && r.height === 0) return null;
  return r;
}

function computeTooltipPos(rect, tipWidth, tipHeight, margin = 12) {
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  // Fallback: center when no target
  if (!rect) {
    return {
      top: Math.max(margin, (vh - tipHeight) / 2),
      left: Math.max(margin, (vw - tipWidth) / 2),
    };
  }
  // Try placing below first, then above
  let top = rect.bottom + margin;
  if (top + tipHeight > vh - margin) {
    top = rect.top - tipHeight - margin;
  }
  // Clamp top
  top = Math.max(margin, Math.min(top, vh - tipHeight - margin));
  // Center horizontally w.r.t. the target, clamp to viewport
  let left = rect.left + rect.width / 2 - tipWidth / 2;
  left = Math.max(margin, Math.min(left, vw - tipWidth - margin));
  return { top, left };
}

export default function OnboardingTour({ open, onClose, lang = "en" }) {
  const [stepIndex, setStepIndex] = useState(0);
  const [rect, setRect] = useState(null);
  const [tick, setTick] = useState(0); // forces recompute on resize/scroll
  const tipRef = useRef(null);

  const isIt = (lang || "en").startsWith("it");
  const steps = buildSteps(isIt);
  const step = steps[stepIndex];
  const totalSteps = steps.length;

  // Recompute target rect whenever step changes or viewport resizes
  useEffect(() => {
    if (!open) return;
    const update = () => {
      setRect(getRect(step.target));
      setTick((t) => t + 1);
    };
    update();
    // Re-run a few times — target may render slightly after the tour opens
    const ids = [setTimeout(update, 120), setTimeout(update, 400), setTimeout(update, 900)];
    window.addEventListener("resize", update);
    window.addEventListener("scroll", update, true);
    return () => {
      ids.forEach(clearTimeout);
      window.removeEventListener("resize", update);
      window.removeEventListener("scroll", update, true);
    };
  }, [open, stepIndex, step.target]);

  // Reset to step 0 every time the tour is re-opened
  useEffect(() => {
    if (open) setStepIndex(0);
  }, [open]);

  const finish = useCallback(() => {
    markOnboardingSeen();
    onClose?.();
  }, [onClose]);

  const handleNext = useCallback(() => {
    if (stepIndex >= totalSteps - 1) {
      finish();
    } else {
      setStepIndex(stepIndex + 1);
    }
  }, [stepIndex, totalSteps, finish]);

  const handleSkip = useCallback(() => finish(), [finish]);

  if (!open) return null;

  // Measure tooltip (fallback dimensions before first render)
  const tipW = 300;
  const tipH = tipRef.current?.offsetHeight || 180;
  const pos = computeTooltipPos(rect, tipW, tipH);

  // Pad target rect for the "hole"
  const pad = 6;
  const holeStyle = rect
    ? {
        top: Math.max(0, rect.top - pad),
        left: Math.max(0, rect.left - pad),
        width: rect.width + pad * 2,
        height: rect.height + pad * 2,
      }
    : null;

  const bg = "rgba(0, 0, 0, 0.72)";

  return ReactDOM.createPortal(
    <div className="fixed inset-0 z-[250]" data-testid="onboarding-tour" aria-live="polite">
      {/* Dimming overlay built from 4 rects around the target. On no target,
          one full-screen rect dims everything. */}
      {rect ? (
        <>
          <div style={{ position: "fixed", top: 0, left: 0, right: 0, height: Math.max(0, rect.top - pad), background: bg }} />
          <div style={{ position: "fixed", top: rect.bottom + pad, left: 0, right: 0, bottom: 0, background: bg }} />
          <div style={{ position: "fixed", top: Math.max(0, rect.top - pad), left: 0, width: Math.max(0, rect.left - pad), height: rect.height + pad * 2, background: bg }} />
          <div style={{ position: "fixed", top: Math.max(0, rect.top - pad), left: rect.right + pad, right: 0, height: rect.height + pad * 2, background: bg }} />
          {/* Highlight border */}
          <div
            style={{
              position: "fixed",
              ...holeStyle,
              border: "2px solid #cfb53b",
              borderRadius: 10,
              boxShadow: "0 0 24px rgba(207,181,59,0.55), inset 0 0 14px rgba(207,181,59,0.25)",
              pointerEvents: "none",
              animation: "onboardingPulse 1.6s ease-in-out infinite",
            }}
          />
        </>
      ) : (
        <div className="fixed inset-0" style={{ background: bg }} />
      )}

      {/* Tooltip card */}
      <div
        ref={tipRef}
        data-testid="onboarding-tooltip"
        style={{
          position: "fixed",
          top: pos.top,
          left: pos.left,
          width: tipW,
          maxWidth: "calc(100vw - 24px)",
        }}
        className="bg-[#1a1a1d] border-2 border-[#cfb53b]/50 rounded-xl shadow-[0_10px_40px_rgba(0,0,0,0.7)] p-4 text-[#e2e8f0]"
      >
        <div className="flex items-start justify-between gap-2 mb-2">
          <div className="flex items-center gap-2 min-w-0">
            <Sparkles size={14} className="text-[#cfb53b] shrink-0" />
            <h3 className="font-heading text-sm sm:text-base text-[#cfb53b] truncate" data-testid="onboarding-title">
              {step.title}
            </h3>
          </div>
          <button
            onClick={handleSkip}
            className="text-[#94a3b8] hover:text-[#cfb53b] transition-colors shrink-0"
            aria-label="Skip tour"
            data-testid="onboarding-skip"
          >
            <X size={16} />
          </button>
        </div>
        <div className="flex gap-3 items-start mb-3">
          {step.expression && (
            <div 
              className="w-12 h-12 rounded-full overflow-hidden border border-[#cfb53b]/30 shrink-0 bg-[#0a0a0c] p-0.5 shadow-[0_0_8px_rgba(168,85,247,0.3)] animate-nyx-float"
              style={{ minWidth: "48px" }}
            >
              <img
                src={`/assets/mascot/nyx_${step.expression}.png?v=2`}
                alt="Nyx"
                className="w-full h-full object-contain"
                onError={(e) => { e.target.style.display = 'none'; }}
              />
            </div>
          )}
          <p className="text-xs sm:text-sm text-[#cbd5e1] leading-relaxed flex-1" data-testid="onboarding-body">
            {step.body}
          </p>
        </div>
        <div className="flex items-center justify-between">
          <span className="text-[10px] text-[#64748b] font-heading uppercase tracking-wider">
            {stepIndex + 1} / {totalSteps}
          </span>
          <div className="flex items-center gap-2">
            <button
              onClick={handleSkip}
              className="text-xs text-[#94a3b8] hover:text-[#cfb53b] px-2 py-1.5 transition-colors"
              data-testid="onboarding-skip-btn"
            >
              {isIt ? "Salta" : "Skip"}
            </button>
            <button
              onClick={handleNext}
              className="flex items-center gap-1 bg-[#cfb53b] hover:bg-[#d4af37] text-[#0a0a0c] font-heading text-xs sm:text-sm px-3 py-1.5 rounded-md transition-colors"
              data-testid="onboarding-next"
            >
              {stepIndex >= totalSteps - 1
                ? (isIt ? "Concludi" : "Finish")
                : (isIt ? "Avanti" : "Next")}
              <ChevronRight size={14} />
            </button>
          </div>
        </div>
      </div>

      {/* Keyframes for the pulse (inlined to avoid global CSS file churn) */}
      <style>{`
        @keyframes onboardingPulse {
          0%, 100% { box-shadow: 0 0 24px rgba(207,181,59,0.55), inset 0 0 14px rgba(207,181,59,0.25); }
          50% { box-shadow: 0 0 36px rgba(207,181,59,0.85), inset 0 0 20px rgba(207,181,59,0.4); }
        }
        @keyframes nyx-float-sm {
          0%, 100% { transform: translateY(0px) rotate(0deg); }
          50% { transform: translateY(-4px) rotate(0.5deg); }
        }
        .animate-nyx-float {
          animation: nyx-float-sm 4s ease-in-out infinite;
        }
      `}</style>
      {/* Tick usage to satisfy lint for a state that exists only to force a
          recompute on resize/scroll. */}
      <span aria-hidden="true" style={{ display: "none" }}>{tick}</span>
    </div>,
    document.body,
  );
}
